import { AxiosHeaders, type RawAxiosRequestHeaders } from 'axios';
import HttpCodes from 'http-status-codes';
import _ from 'lodash';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { removeRequest } from '@/requests/axios.actions';
import { getAxiosInstance } from '@/requests/axios.utilities';
import { generateRequestId } from '@/utilities/http.utilities';
import type { SeeqInternalAxiosRequestConfig } from '@/requests/axios.utilities';

type RetryRequestHeaders = SeeqInternalAxiosRequestConfig['headers'] | RawAxiosRequestHeaders | undefined;
type AxiosHeadersInit = Parameters<typeof AxiosHeaders.from>[0];

type RetryRequestConfig = Partial<Omit<SeeqInternalAxiosRequestConfig, 'headers'>> & {
  retry?: number;
  headers?: RetryRequestHeaders;
};

type RetryableSeeqRequestConfig = SeeqInternalAxiosRequestConfig & {
  retry?: number;
};

type ConflictAxiosError = {
  response?: {
    status?: number;
    config?: RetryRequestConfig;
  };
};

export function conflictRetryInterceptor(error?: ConflictAxiosError): Promise<unknown> {
  const MAXIMUM_RETRIES = 5;
  if (!error) {
    return Promise.reject(error);
  }

  const responseStatus = _.get(error, 'response.status');
  if (responseStatus !== HttpCodes.CONFLICT || !error.response?.config) {
    return Promise.reject(error);
  }

  const { config } = error.response;
  config.retry = config.retry || 0;

  if (config.retry < MAXIMUM_RETRIES) {
    // Before we retry, we need to remove the previous request from the pending requests list.
    // This is necessary because we don't issue a rejection, which means that the downstream
    // interceptor where we normally remove requests (decrementPendingRequestsError) won't be called.
    removeRequest(config.headers?.[SeeqNames.API.Headers.RequestId]);

    config.retry += 1;
    const normalizedHeaders = normalizeHeaders(config.headers);
    normalizedHeaders.set(SeeqNames.API.Headers.RequestId, generateRequestId());

    const retryConfig: RetryableSeeqRequestConfig = { ...config, headers: normalizedHeaders };
    return Promise.resolve().then(() => getAxiosInstance().request(retryConfig));
  }

  return Promise.reject(error);
}

function normalizeHeaders(headers?: RetryRequestHeaders) {
  if (!headers) {
    return new AxiosHeaders();
  }
  if (headers instanceof AxiosHeaders) {
    return AxiosHeaders.from(headers);
  }
  return AxiosHeaders.from(headers as AxiosHeadersInit);
}
